<template>
  <div>
    <div class="modal-container">
      <Dialog :visible="showConfirmDeleteDraft" :modal="true" :style="{ width: isMD ? '70vw' : '500px' }">
        <template #header>
          <div class="flex gap-4">
            <SvgIcon name="trash" size="1.5rem" class="mt-1" />
            <div style="font-size: 18px" class="font-bold">
              Are you sure you want to delete the draft application {{ forDeleteItem.loanReferenceNumber }}?
            </div>
          </div>
        </template>
        <template #footer>
          <div class="modal-footer">
            <Button
              label="Confirm delete"
              :loading="isLoading"
              type="primary"
              @click="confirmDeleteDraft(forDeleteItem.id)"
            />
            <p class="close" @click="showConfirmDeleteDraft = false">Cancel</p>
          </div>
        </template>
      </Dialog>
      <Dialog :visible="showTermsAndConditionsModal" :modal="true" :style="{ width: isMD ? '90vw' : '600px' }">
        <template #header>
          <div style="font-size: 18px" class="font-bold">Confirm compliance requirements</div>
        </template>
        <div class="terms-content">
          <div class="terms-text">
            <p>
              Before resuming this application, please confirm that you have completed all necessary aggregator
              compliance requirements, including but not limited to:
            </p>
            <ul class="compliance-list">
              <li><span>Privacy and Consent form signed by client/s.</span></li>
              <li><span>Credit Guide provided to the client/s.</span></li>
              <li>
                <span>Reasonable Enquiries and Responsible Lending Requirements have been met under NCCP.</span>
              </li>
              <li><span>Best Interests Duty requirements have been completed.</span></li>
            </ul>
          </div>
          <div class="checkbox-wrapper">
            <div class="p-field-checkbox">
              <Checkbox id="termsAgreement" name="termsAgreement" v-model="hasAgreedToTerms" :binary="true" />
              <label class="terms-label" for="termsAgreement">
                Please tick to confirm that you have met all compliance requirements.
              </label>
            </div>
          </div>
          <div v-if="showTermsError" class="terms-error">
            <img src="@/assets/alert-triangle.svg" />
            <p class="ml-2">
              Please confirm that you have completed all necessary aggregator compliance requirements to proceed.
            </p>
          </div>
        </div>
        <template #footer>
          <div class="modal-footer">
            <p class="close" @click="closeTermsModal">Cancel</p>
            <Button label="Continue" :loading="isLoading" type="primary" @click="proceedWithApplication" />
          </div>
        </template>
      </Dialog>
    </div>
    <Card class="position-relative">
      <template #title>
        <div class="align-items-center justify-content-between" :class="isMD ? '' : 'flex'">
          <div class="align-items-center justify-content-center type-dropdown" :class="isMD ? '' : 'flex'">
            <img src="@/assets/applications.svg" />
            <div class="flex justify-content-start">
              <ChipInput
                :inputClass="isMD ? 'my-2' : 'w-full'"
                placeholder="Select deal types"
                :selectedItems="applicationSaveTypes"
                :options="applicationSaveTypeOptions"
                @change="applicationSaveTypeChanged($event)"
                selectAllLabel="All types"
              />
            </div>
          </div>
          <div class="align-items-center" :class="isMD ? '' : 'flex'">
            <FilterComponent @status="getStatus" @stage="getStage" />
            <Datefilter
              class="datefilter-component"
              numberOfMonths="2"
              @filter="getDate"
              :class="isMD ? 'mt-2' : 'ml-2'"
              :key="filterKey"
              @clear="onDateFilterClear"
              :value="this.fromDate && this.toDate ? [this.fromDate, this.toDate] : ''"
            />
            <span class="p-input-icon-left" :class="isMD ? 'mt-2' : 'ml-2'">
              <i class="pi pi-search" />
              <InputText @input="getSearchString($event)" type="text" :value="this.searchString" placeholder="Search" />
            </span>
          </div>
        </div>
      </template>
      <template #content>
        <Spinner v-if="isLoading" class="flex" />
        <DataTable
          v-if="!isLoading && !isMD"
          v-model:selection="selected"
          :value="applicationList"
          :totalRecords="applications.length"
          key="data-table-application-list"
        >
          <Column field="loanReferenceNumber" sortable header="ID" headerStyle="width: 2.5rem">
            <template #body="{ data }">
              {{ data.loanReferenceNumber }}
            </template>
          </Column>
          <Column sortable field="date" header="Date" />
          <Column sortable field="properties" header="Property details">
            <template #body="{ data }">
              <div v-for="(property, propertyIndex) in data.properties" :key="propertyIndex" class="property">
                <div v-for="(line, idx) in property" :key="idx" :class="{ 'table-item-label': idx === 0 }">
                  {{ line }}
                </div>
              </div>
            </template>
          </Column>
          <Column v-if="userType === 'broker_assistant'" sortable field="brokerDetail" header="Broker">
            <template #body="{ data }">
              <div>
                {{ [data.broker.firstName, data.broker.lastName].filter(Boolean).join(' ') }}
              </div>
              <div>
                {{ data.broker.brokerReferenceNumber }}
              </div>
            </template>
          </Column>
          <Column sortable field="applicants" header="Customer details">
            <template #body="{ data }">
              <div v-for="(applicantName, applicantIndex) in data.applicants" :key="applicantIndex">
                {{ applicantName }}
              </div>
            </template>
          </Column>

          <Column sortable field="loanAmount" header="Application details">
            <template #body="{ data }">
              <span v-if="data.applicationSaveType === applicationSaveTypeConstant.LOAN_SUBMISSION">
                <div class="table-item-label">
                  {{ hideTotalSetupFee(data.application.crmStatus) ? 'Estimated loan amount' : 'Loan amount' }}
                </div>
                <div>
                  {{ data.loanAmount }}
                </div>
                <div class="table-item-label mt-2">Broker fee</div>
                <div>
                  <div>{{ data.brokerFeeRate }}</div>
                  <div>{{ hideTotalSetupFee(data.application.crmStatus) ? 'TBD' : data.brokerFeeAmount }}</div>
                </div>
              </span>
            </template>
          </Column>
          <Column class="capital" sortable field="loanStage" header="Loan stage">
            <template #body="{ data }">
              <span> {{ buttonData(data).stage }} </span>
            </template>
          </Column>
          <Column field="applicationType" header="">
            <template #body="{ data }">
              <div class="flex justify-content-end action-button-wrapper">
                <SplitButton
                  v-if="data.applicationSaveType === applicationSaveTypeConstant.DRAFT"
                  :label="buttonData(data).buttonLabel"
                  :icon="buttonData(data).buttonIcon"
                  type="secondary"
                  button_class="action-button"
                  @click="buttonData(data).action"
                  :model="buttonData(data).splitButtonActions"
                />
                <Button
                  v-else
                  :label="buttonData(data).buttonLabel"
                  :icon="buttonData(data).buttonIcon"
                  type="secondary"
                  button_class="action-button"
                  @click="buttonData(data).action"
                />
              </div>
            </template>
          </Column>
        </DataTable>
        <Paginator
          v-if="!isMD"
          class="mt-2"
          :rows="5"
          :totalRecords="applicationsCount"
          :rowsPerPageOptions="[5, 10, 20, 30]"
          template="RowsPerPageDropdown  CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
          currentPageReportTemplate="Showing {first} of {totalRecords} applications"
          @page="onChangePage($event)"
        />
        <div v-if="!isLoading && isMD">
          <div class="application-card-container" v-for="(item, i) in applicationList" :key="i">
            <div class="flex align-items-center col col-12 gap-4">
              <div>
                {{ item.loanReferenceNumber }}
              </div>
              <div class="flex w-full justify-content-end">
                <SplitButton
                  v-if="item.applicationSaveType === applicationSaveTypeConstant.DRAFT"
                  :label="buttonData(item).buttonLabel"
                  :icon="buttonData(item).buttonIcon"
                  type="secondary"
                  button_class="action-button"
                  @click="buttonData(item).action"
                  :model="buttonData(item).splitButtonActions"
                />
                <Button
                  v-else
                  :label="buttonData(item).buttonLabel"
                  :icon="buttonData(item).buttonIcon"
                  type="secondary"
                  button_class="action-button"
                  @click="buttonData(item).action"
                />
              </div>
            </div>
            <div class="full-width">
              <div class="flex full-width">
                <div class="col col-6">
                  <div class="title">Date</div>
                  <div>{{ item.date }}</div>
                </div>
                <div class="col col-6 capital">
                  <div class="title">Loan stage</div>
                  <span> {{ buttonData(item).stage }} </span>
                </div>
              </div>
              <div class="flex flex-wrap full-width">
                <div class="col col-12">
                  <div class="title">Customer details</div>
                  <div v-for="(applicantName, applicantIndex) in item.applicants" :key="applicantIndex">
                    {{ applicantName }}
                  </div>
                </div>
                <div class="col col-12">
                  <div class="title mb-1">Property details</div>
                  <div v-for="(property, propertyIndex) in item.properties" :key="propertyIndex" class="property">
                    <div v-for="(line, idx) in property" :key="idx" :class="{ 'table-item-label': idx === 0 }">
                      {{ line }}
                    </div>
                  </div>
                </div>
                <div v-if="item.type === applicationSaveTypeConstant.LOAN_SUBMISSION" class="col col-12">
                  <div class="title">Application details</div>
                  <div class="flex justify-content-between line-detail">
                    <span>Loan amount:</span>
                    <span>{{ item.loanAmount }}</span>
                  </div>
                </div>
                <div v-if="item.type === applicationSaveTypeConstant.LOAN_SUBMISSION" class="col col-12">
                  <div class="title">Brokerage</div>
                  <div class="flex justify-content-between line-detail">
                    <span>Rate:</span>
                    <span>{{ item.brokerFeeRate }}</span>
                  </div>
                  <div class="flex justify-content-between line-detail">
                    <span>Amount:</span>
                    <span>{{ item.brokerFeeAmount }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import { API, LOAN_APPLICATION_URL, TECHLEND_BE_URL } from '@/constants.api';
import { APPLICATION_SAVE_TYPE } from '@/constants';
import { _delete, _get } from '@/services/crudService';
import { debounce } from 'debounce';
import { formatCurrency, formatDate, formatNumber, fromSnakeCase } from '@/services/formatHelper';
import { isStatusBefore } from '@/services/statusService';
import { mapActions, mapGetters } from 'vuex';
import { useCookies } from 'vue3-cookies';
import Card from 'primevue/card';
import Checkbox from 'primevue/checkbox';
import ChipInput from '../../../components/common/ChipInput.vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Datefilter from './Datefilter.vue';
import Dialog from 'primevue/dialog';
import FilterComponent from './Filter.vue';
import InputText from 'primevue/inputtext';
import Paginator from 'primevue/paginator';
import ScreenSize from '@/mixins/screensize.mixin';
import SvgIcon from '../../../components/common/SvgIcon.vue';

const applicationSaveTypeOptions = [
  {
    name: 'Applications',
    code: APPLICATION_SAVE_TYPE.LOAN_SUBMISSION,
  },
  {
    name: 'Drafts',
    code: APPLICATION_SAVE_TYPE.DRAFT,
  },
  {
    name: 'Scenarios',
    code: APPLICATION_SAVE_TYPE.SCENARIO,
  },
];

export default {
  name: 'index',
  mixins: [ScreenSize],
  components: {
    DataTable,
    Column,
    Card,
    Checkbox,
    ChipInput,
    InputText,
    Datefilter,
    FilterComponent,
    Paginator,
    SvgIcon,
    Dialog,
  },
  setup() {
    return {
      formatCurrency,
      formatDate,
      formatNumber,
      fromSnakeCase,
    };
  },
  data() {
    return {
      showConfirmDeleteDraft: false,
      showTermsAndConditionsModal: false,
      forDeleteItem: {},
      pendingApplicationData: null,
      hasAgreedToTerms: false,
      showTermsError: false,
      applications: [],
      isLoading: true,
      fromDate: '',
      toDate: '',
      searchString: '',
      statusList: [],
      stageList: [],
      applicationsCount: 0,
      selected: [],
      rowsPerPage: 5,
      applicationSaveTypes: [...applicationSaveTypeOptions],
      applicationSaveTypeOptions: [...applicationSaveTypeOptions],
      applicationSaveTypeConstant: APPLICATION_SAVE_TYPE,
    };
  },
  computed: {
    ...mapGetters({
      userType: 'userType',
    }),
    applicationList() {
      const formatAddress = (type) => (property) => {
        const line1 = [property.line1, property.line2].filter(Boolean).join(', ');
        const line2 = [property.city, property.state?.toUpperCase()].filter(Boolean).join(', ');

        return [type, line1, line2, property.postCode].filter(Boolean);
      };
      const formatDraftAddress = (type) => (property) => {
        const parts = property.split(',');
        const trimmedParts = parts.map((part) => part.trim());
        if (trimmedParts.length !== 4) {
          return [type, property];
        }
        const line1 = trimmedParts.slice(0, 2);
        const subAddress = trimmedParts.slice(-2);
        const state = subAddress
          .map((item, index) => {
            if (index === subAddress.length - 1) {
              const parts = item.split(' ');
              parts.pop();
              return parts.join(' ');
            }
            return item;
          })
          .join(', ');
        return [type, line1.join(', '), state];
      };
      const formatName = (applicant) => [applicant.firstName, applicant.lastName].filter(Boolean).join(' ');
      return this.applications.map((app) => {
        const loanAmount = app.calculatedValue.loanAmount ?? 0;
        const brokerFeeRate = app.calculatedValue.brokerFee ?? 0;

        return {
          id: app.id,
          loanReferenceNumber: app.loanReferenceNumber,
          date: formatDate(app.applicationDate),
          broker: app.broker,
          properties:
            app.applicationSaveType === APPLICATION_SAVE_TYPE.LOAN_SUBMISSION
              ? [
                  ...app.incomingProperties.map(formatAddress('Incoming')),
                  ...app.outgoingProperties.map(formatAddress('Outgoing')),
                ]
              : [
                  ...app.draftIncomingProperties.map(formatDraftAddress('Incoming')),
                  ...app.draftOutgoingProperties.map(formatDraftAddress('Outgoing')),
                ],
          applicants:
            app.applicationSaveType === APPLICATION_SAVE_TYPE.LOAN_SUBMISSION
              ? app.applicants.map(formatName)
              : app.draftApplicants,
          loanStage: app.applicationStatus,
          loanAmount: formatCurrency(loanAmount),
          brokerFeeRate: formatNumber(brokerFeeRate * 100) + '%',
          brokerFeeAmount: formatCurrency(loanAmount * brokerFeeRate),
          brokerId: app.brokerId,
          applicationSaveType: app.applicationSaveType,
          applicationType: app.applicationType,
          application: app,
        };
      });
    },
  },
  methods: {
    ...mapActions({
      setApplicationId: 'applications/setApplicationId',
      setLoanReferenceNumber: 'applications/setLoanReferenceNumber',
    }),
    showDeleteDraftModal(data) {
      this.forDeleteItem = data;
      this.showConfirmDeleteDraft = true;
    },
    hideTotalSetupFee(status) {
      return isStatusBefore(status, 'conditional_offer');
    },
    buttonData(data) {
      if (data.applicationSaveType === APPLICATION_SAVE_TYPE.DRAFT) {
        return {
          buttonIcon: 'pi pi-replay',
          buttonLabel: 'Resume application',
          stage: 'Draft',
          action: () => this.goToApplicationDraft(data),
          splitButtonActions: [
            {
              label: 'Delete draft',
              icon: 'pi pi-trash',
              command: () => {
                this.showDeleteDraftModal(data);
              },
            },
          ],
        };
      }
      if (data.applicationType === APPLICATION_SAVE_TYPE.SCENARIO) {
        return {
          buttonIcon: 'pi pi-refresh',
          buttonLabel: 'Convert to application',
          stage: 'Scenario',
          action: () => this.goToScenario(data),
          splitButtonActions: [],
        };
      }
      if (data.loanStage === 'loan_funded') {
        return {
          buttonIcon: 'pi pi-eye',
          buttonLabel: 'View loan',
          stage: fromSnakeCase(data.loanStage),
          action: () => this.goToProgress(data),
          splitButtonActions: [],
        };
      }
      return {
        buttonIcon: 'pi pi-eye',
        buttonLabel: 'View application',
        stage: fromSnakeCase(data.loanStage),
        action: () => this.goToProgress(data),
        splitButtonActions: [],
      };
    },
    async confirmDeleteDraft(applicationDraftId) {
      if (!applicationDraftId) {
        return;
      }
      this.isLoading = true;
      await _delete(`${TECHLEND_BE_URL}/brokers/applications-draft/delete-application-draft?id=${applicationDraftId}`);
      await this.doFilter();
      this.showConfirmDeleteDraft = false;
    },
    async getStatus(value) {
      this.statusList = value;
      await this.doFilter();
    },
    async getStage(value) {
      this.stageList = value;
      await this.doFilter();
    },
    async goToProgress(data) {
      await this.setApplicationId(data.id);
      await this.setLoanReferenceNumber(data.loanReferenceNumber);
      if (
        data.status == 'cancelled' ||
        data.loan_status === 'dropped_off' ||
        data.loan_status === 'withdrawn' ||
        data.loan_status === 'rejected'
      ) {
        this.$router.push(`/application/summary/${data.loanReferenceNumber}?progress=false`);
      } else {
        this.$router.push(`/application/progress/${data.loanReferenceNumber}?progress=true`);
      }
    },

    setBrokeCookiesAndParameter(data) {
      let brokerIdParameter = '';
      if (this.userType === 'broker_assistant') {
        const { cookies } = useCookies();
        const tokenKey = 'broker-id';
        const hostname = window.location.hostname;
        const domainParts = hostname.split('.');
        let baseDomain;
        if (domainParts.length < 2) {
          baseDomain = null;
        } else {
          const tld = domainParts.slice(-2).join('.');
          const secondLevelDomain = domainParts.slice(-3, -2).join('.');
          baseDomain = domainParts.length > 2 && tld.includes('.') ? `${secondLevelDomain}.${tld}` : tld;
        }
        if (baseDomain) {
          cookies.set(tokenKey, data.brokerId, null, '/', baseDomain, true, 'None');
        } else {
          cookies.set(tokenKey, data.brokerId);
        }
        brokerIdParameter = `&brokerId=${data.brokerId}`;
      }
      return brokerIdParameter;
    },
    goToApplicationDraft(data) {
      this.pendingApplicationData = data;
      this.hasAgreedToTerms = false;
      this.showTermsError = false;
      this.showTermsAndConditionsModal = true;
    },
    closeTermsModal() {
      this.showTermsAndConditionsModal = false;
      this.hasAgreedToTerms = false;
      this.showTermsError = false;
      this.pendingApplicationData = null;
    },
    proceedWithApplication() {
      if (!this.hasAgreedToTerms) {
        this.showTermsError = true;
        return;
      }
      this.showTermsError = false;
      this.showTermsAndConditionsModal = false;
      const brokerIdParameter = this.setBrokeCookiesAndParameter(this.pendingApplicationData);
      window.open(
        `${LOAN_APPLICATION_URL}?applicationDraftId=${this.pendingApplicationData.id}${brokerIdParameter}`,
        '_blank',
      );
      this.pendingApplicationData = null;
      this.hasAgreedToTerms = false;
    },
    goToScenario(data) {
      const brokerIdParameter = this.setBrokeCookiesAndParameter(data);
      window.open(`${LOAN_APPLICATION_URL}?scenarioApplicationId=${data.id}${brokerIdParameter}`, '_blank');
    },
    async doFilter() {
      await this.loadData(
        0,
        this.rowsPerPage,
        this.fromDate,
        this.toDate,
        this.statusList,
        this.stageList,
        this.searchString,
        this.applicationSaveTypes,
      );
    },
    getSearchString: debounce(async function ({ target }) {
      this.searchString = target.value;
      await this.doFilter();
    }, 800),
    async applicationSaveTypeChanged(value) {
      this.applicationSaveTypes = value;
      await this.doFilter();
    },
    async onDateFilterClear() {
      this.fromDate = '';
      this.toDate = '';
      this.filterKey += 1;
      await this.doFilter();
    },
    async getDate(value) {
      this.fromDate = new Date(value[0]);
      this.toDate = new Date(value[1]);
      this.filterKey += 1;
      await this.doFilter();
    },
    async onChangePage(value) {
      this.rowsPerPage = Number(value.rows);
      await this.loadData(
        Number(value.page) * this.rowsPerPage,
        this.rowsPerPage,
        this.fromDate,
        this.toDate,
        this.statusList,
        this.stageList,
        this.searchString,
        this.applicationSaveTypes,
      );
    },

    async loadData(offset, limit, fromDate, toDate, applicationStatuses, crmStatuses, search, applicationSaveTypes) {
      this.isLoading = true;
      let query = `?offset=${offset}&limit=${limit}`;
      query += applicationSaveTypes?.length
        ? `&applicationSaveTypes=${applicationSaveTypes.map((item) => item.code).join(',')}`
        : '';
      query += fromDate ? `&startDate=${new Date(fromDate).toISOString().split('T')[0]}` : '';
      query += toDate ? `&endDate=${new Date(toDate).toISOString().split('T')[0]}` : '';
      query += applicationStatuses?.length ? `&loanStatuses=${applicationStatuses}` : '';
      query += crmStatuses?.length ? `&crmStatuses=${crmStatuses}` : '';
      query += search ? `&search=${encodeURIComponent(search)}` : '';
      try {
        const result = await _get(TECHLEND_BE_URL + API.APPLICATIONS.LIST_APPLICATIONS + query);
        this.applications = result?.data?.applications ? result?.data?.applications : [];
        this.applicationsCount = result?.data?.count ? Number(result?.data?.count) : 0;
        this.applications.map((application) => {
          application.customer = application.applicants.map((item) => {
            if (item.applicantType == 'primary') {
              return item.firstName;
            }
          });
          return application;
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
  watch: {
    selected(items) {
      this.$emit(
        'list',
        items.map((item) => item.application),
      );
    },
  },
  async created() {
    const scenarioId = this.$route.query.scenarioId;
    const draftId = this.$route.query.draftId;
    const loanId = this.$route.query.loanId;

    if (loanId) {
      this.applicationSaveTypes = [applicationSaveTypeOptions.find((item) => item.code == 'loan_submission')];
      this.searchString = loanId;
    } else if (scenarioId) {
      this.applicationSaveTypes = [applicationSaveTypeOptions.find((item) => item.code == 'scenario')];
      this.searchString = scenarioId;
    } else if (draftId) {
      this.applicationSaveTypes = [applicationSaveTypeOptions.find((item) => item.code == 'draft')];
      this.searchString = draftId;
    }

    await this.loadData(0, this.rowsPerPage, null, null, null, null, this.searchString, this.applicationSaveTypes);
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/global.scss';
.modal-footer {
  gap: 2rem;
  display: flex;
  align-items: center;
  justify-content: flex-end;

  .close {
    margin: 0;
    cursor: pointer;
  }
}

.action-icon {
  margin-left: -6px;
  cursor: pointer;
  color: $pink-color;
  background-color: white;
  padding: 0.3rem;
  border-radius: 4px;
  &:hover {
    transition: all 0.5s;
    color: white;
    background-color: $pink-color;
  }
}

.type-dropdown {
  :deep(.p-float-label) {
    display: none !important;
  }
}
.property + .property {
  margin-top: 1rem;
}
.table-item-label {
  color: rgba(2, 0, 56, 0.3);
  font-size: 0.75rem;
  text-transform: uppercase;
}

.line-detail {
  max-width: 300px;
}

:deep(.p-card-body) {
  overflow-y: auto !important;
  overflow-x: hidden !important;
}
.p-card {
  box-shadow: unset !important;
  border-radius: 4px !important;
  color: $primary-text-color !important;
  font-family: $font-family !important;
}
:deep(.p-datatable-wrapper) {
  overflow-x: auto;
}
:deep(.p-card-title) {
  font-size: 1.25rem !important;
}
:deep(.p-datatable .p-datatable-thead > tr > th) {
  background: $white-color;
  color: rgba(2, 0, 56, 0.3) !important;
  font-size: 1rem;
}
:deep(.p-datatable .p-datatable-tbody > tr > td) {
  font-weight: 500 !important;
  font-size: 14px !important;
  color: $secondary-color !important;
  font-family: HK-medium !important;
  vertical-align: top !important;
}
.color-highlight {
  color: $primary-color !important;
  cursor: pointer;
}
:deep(.p-paginator .p-paginator-pages .p-paginator-page.p-highlight) {
  background: unset !important;
  color: $primary-color !important;
  border-color: unset !important;
}
:deep(.p-link:focus) {
  box-shadow: unset !important;
}
:deep(.p-datepicker table td) {
  padding: unset !important;
}
:deep(.p-paginator .p-paginator-current) {
  color: rgba(2, 0, 56, 0.3);
  font-weight: 500;
  font-size: 14px;
}
.datefilter-component {
  width: 232px;
}
:deep(.p-inputtext) {
  font-family: HK-medium !important;
}
.p-inputtext:enabled:focus {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 0.1rem #ced4da !important;
  border-color: #ced4da !important;
}
:deep(.p-column-title) {
  white-space: nowrap !important;
}

:deep(.p-checkbox .p-checkbox-box.p-highlight) {
  background: $primary-color;
  border-color: $primary-color;

  &:hover {
    background: darken($primary-color, 15%) !important;
    border-color: darken($primary-color, 15%) !important;
  }
}
:deep(.p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box.p-focus) {
  outline: 0 none;
  outline-offset: 0;
  box-shadow: 0 0 0 0.1rem #ced4da;
  border-color: #ced4da !important;
}
:deep(.p-checkbox:not(.p-checkbox-disabled) .p-checkbox-box:hover) {
  border-color: #ced4da !important;
}
.application-card-container {
  font-weight: 500;
  font-size: 14px;
  border-bottom: 1px solid rgba(2, 0, 56, 0.15);
  .title {
    color: rgba(2, 0, 56, 0.4);
  }
  margin-right: 1rem;
}

.action-button-wrapper {
  position: relative;
  button {
    margin-left: 0;
  }
}

.action-button {
  text-align: left;
  font-size: 14px;
}

.terms-content {
  padding: 1rem 0;
}

.terms-text {
  margin-bottom: 1.5rem;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 0.5rem;
}

.terms-text p {
  margin-bottom: 1rem;
  font-size: 1rem;
  line-height: 1.5;
}

.terms-text ol,
.terms-text ul {
  padding-left: 1.5rem;
}

.terms-text li {
  margin-bottom: 0.75rem;
  line-height: 1.5;
}

.compliance-list {
  list-style: none;
  padding-left: 0;
}

.compliance-list li {
  position: relative;
  padding-left: 1.5rem;
}

.compliance-list li::before {
  content: '-';
  position: absolute;
  left: 0;
  color: rgba(2, 0, 56, 0.6);
  font-weight: bold;
}

.checkbox-wrapper {
  margin-bottom: 1rem;
}

.p-field-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
}

.terms-label {
  margin: 0;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1.5;
}

.terms-error {
  display: flex;
  align-items: center;
  border: 1px solid #f44336;
  padding: 1rem;
  border-radius: 5px;
  color: #f44336;
  margin-top: 1rem;
}

.terms-error p {
  margin: 0;
  font-size: 0.875rem;
}

:deep(.p-checkbox) {
  margin-top: 0.25rem;
}
</style>